import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper, Search, RotateCcw } from "lucide-react";
import "../../styles/news-hub.css";

function NewsImage({ item, lead }) {
  const [failed, setFailed] = useState(false);
  const source = (String(item.source || "").trim() || "XPHub");
  const monogram = source.length <= 4 ? source.toUpperCase() : source.split(/\s+/).slice(0, 2).map(word => word[0]).join("").toUpperCase();
  return (
    <div
      className={`news-story__image ${!item.imageUrl || failed ? "news-story__image--empty" : ""}`}
    >
      {item.imageUrl && !failed ? (
        <img
          src={item.imageUrl}
          alt=""
          loading={lead ? "eager" : "lazy"}
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="news-story__fallback" aria-hidden="true">
          <span className="news-story__edition">{item.sourceType === "steam" ? "ACTUALIZACIONES" : "ACTUALIDAD"}</span>
          <strong className="news-story__monogram">{monogram}</strong>
          <span className="news-story__publisher">{source}</span>
          <span className="news-story__imprint">XP<span>HUB</span> / NOTICIAS</span>
        </div>
      )}
    </div>
  );
}

function NewsStory({ item, lead, location, formatDate }) {
  const internal = item.sourceType === "steam" && item.appid && item.gid;
  const Tag = internal ? Link : "a";
  const linkProps = internal
    ? { to: `/news/${item.appid}/${item.gid}`, state: { background: location } }
    : { href: item.url, target: "_blank", rel: "noopener noreferrer" };
  const date =
    item.date && Number.isFinite(Date.parse(item.date)) ? item.date : null;
  return (
    <article className={`news-story ${lead ? "news-story--lead" : ""}`}>
      <Tag {...linkProps} className="news-story__link">
        <NewsImage key={item.imageUrl} item={item} lead={lead} />
        <div className="news-story__body">
          <div className="news-story__meta">
            <span>{item.source}</span>
            {date && <time dateTime={date}>{formatDate(date)}</time>}
          </div>
          <h2>{item.title}</h2>
          {item.snippet && <p>{item.snippet}</p>}
          <span className="news-story__action">
            {internal ? "Leer noticia" : "Leer en el medio"}
            <ArrowUpRight size={16} aria-hidden="true" />
            {!internal && (
              <span className="news-sr-only"> (abre en otra pestaña)</span>
            )}
          </span>
        </div>
      </Tag>
    </article>
  );
}

export default function NewsFeedView({
  items,
  status,
  errorMsg,
  degradedMsg,
  availableSources,
  searchInput,
  setSearchInput,
  selectedSource,
  setSelectedSource,
  selectedLanguage,
  setSelectedLanguage,
  clearFilters,
  hasActiveFilters,
  triggerRetry,
  location,
  formatDate,
}) {
  const filtering = hasActiveFilters || searchInput.trim();
  const sources =
    selectedSource !== "all" && !availableSources.includes(selectedSource)
      ? [selectedSource, ...availableSources]
      : availableSources;
  return (
    <section className="news-feed" aria-label="Noticias recientes">
      <div className="news-filters">
        <label className="news-search">
          <Search size={18} aria-hidden="true" />
          <span className="news-sr-only">Buscar noticias</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar un juego o un tema"
          />
        </label>
        <label className="news-source">
          <span>Fuente</span>
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
          >
            <option value="all">Todas las fuentes</option>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </label>
        <div
          className="news-languages"
          role="group"
          aria-label="Idioma de las noticias"
        >
          {[
            { value: "all", label: "Todos" },
            { value: "es", label: "Español" },
            { value: "en", label: "Inglés" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={selectedLanguage === option.value}
              onClick={() => setSelectedLanguage(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="news-feed__toolbar">
        <p role="status">
          {status === "loading"
            ? "Buscando noticias…"
            : status === "error"
              ? "No se ha podido actualizar"
              : `${items.length} ${items.length === 1 ? "noticia" : "noticias"}${filtering ? " con estos filtros" : " disponibles"}`}
        </p>
        {filtering && (
          <button
            type="button"
            className="news-text-button"
            onClick={clearFilters}
          >
            Limpiar filtros
          </button>
        )}
      </div>
      {degradedMsg && status === "success" && (
        <p role="status" className="news-notice">
          {degradedMsg}
        </p>
      )}
      {status === "loading" && (
        <div className="news-loading" aria-hidden="true">
          <div />
          <div />
          <div />
        </div>
      )}
      {status === "error" && (
        <div className="news-state" role="alert">
          <Newspaper size={32} aria-hidden="true" />
          <h2>No pudimos cargar las noticias</h2>
          <p>{errorMsg}</p>
          <button type="button" onClick={triggerRetry}>
            <RotateCcw size={16} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      )}
      {status === "empty" && (
        <div className="news-state">
          <Newspaper size={32} aria-hidden="true" />
          <h2>
            {filtering
              ? "No encontramos noticias con esos filtros"
              : "Todavía no hay noticias disponibles"}
          </h2>
          <p>
            {filtering
              ? "Prueba otro tema o amplía la selección de fuentes e idiomas."
              : "Vuelve a consultar dentro de un rato."}
          </p>
          <button
            type="button"
            onClick={filtering ? clearFilters : triggerRetry}
          >
            {filtering ? "Ver todas las noticias" : "Actualizar"}
          </button>
        </div>
      )}
      {status === "success" && (
        <div
          className={`news-feed__stories ${filtering ? "news-feed__stories--filtered" : ""}`}
          aria-busy="false"
        >
          {items.map((item, index) => (
            <NewsStory
              key={item.id}
              item={item}
              lead={!filtering && index === 0}
              location={location}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
      <footer className="news-feed__footer">
        Noticias de medios y actualizaciones de Steam. Cada artículo conserva su
        fuente original.
      </footer>
    </section>
  );
}
